import * as Sentry from '@sentry/react';

import {
  EnergyQuoteType,
  ProductSubtype,
  ProductType,
} from '@generated/gql/graphql';
import {
  EnergyQuote,
  OutdatedBundleModalVariant,
  UpdatedTariffModalVariant,
} from '@src/types/bundle';
import { isEnergyQuote } from '@utils/typeCheck';

import {
  CategorizedProducts,
  CategorizedQuotes,
  ModalState,
  ProductKeys,
} from './types';
import { comparePrices, getMonthlyCostValue } from './utils';

const getProductVariant = (quotes: CategorizedQuotes) => {
  const { expired, available } = quotes;

  if (expired.length === 0) {
    return OutdatedBundleModalVariant.none;
  }

  if (available.length > 0) {
    return OutdatedBundleModalVariant.productsChanged;
  }

  return OutdatedBundleModalVariant.productsUnavailable;
};

const compareEnergyPrices = (
  expired: EnergyQuote[],
  available: EnergyQuote[]
): number => {
  const selectedFuels: ProductSubtype[] = expired.map(
    ({ product: { subtype } }) => subtype
  );
  const selectedTariff: EnergyQuoteType = expired[0].product.details.quoteType;

  // Both fixed and variable might be available
  // We compare only these that has the same tariff as selected expired quotes
  const availableToCompare = available.filter(
    ({ product: { subtype, details } }) =>
      selectedFuels.includes(subtype) && details?.quoteType === selectedTariff
  );

  if (availableToCompare.length === 0) {
    return 0;
  }

  return comparePrices(
    getMonthlyCostValue(expired),
    getMonthlyCostValue(availableToCompare)
  );
};

const getEnergyModalVariant = (
  energyQuotes: CategorizedQuotes,
  outdatedModalVariant: OutdatedBundleModalVariant
) => {
  const { expired, available } = energyQuotes;

  if (expired.length === 0) {
    return UpdatedTariffModalVariant.initial;
  }

  // Should never happen
  if (
    available.length === 0 ||
    !expired.every(isEnergyQuote) ||
    !available.every(isEnergyQuote)
  ) {
    Sentry.captureException(
      new Error('useOutdatedBundleModal: invalid quotes state')
    );
    return null;
  }

  if (outdatedModalVariant === OutdatedBundleModalVariant.productsUnavailable) {
    return UpdatedTariffModalVariant.bundleExpired;
  }

  const priceComparison = compareEnergyPrices(expired, available);

  if (priceComparison > 0) {
    return UpdatedTariffModalVariant.priceDecreased;
  }

  if (priceComparison < 0) {
    return UpdatedTariffModalVariant.priceIncreased;
  }

  return UpdatedTariffModalVariant.bundleExpired;
};

const getModalCharacteristics = (
  categorizedProducts: CategorizedProducts
): ModalState => {
  const { removedProducts, hasProductsChanged } = (
    Object.entries(categorizedProducts) as [ProductKeys, CategorizedQuotes][]
  ).reduce(
    (acc, [product, quotes]) => {
      if (product === ProductType.Energy) {
        return acc;
      }

      const variant = getProductVariant(quotes);

      if (variant === OutdatedBundleModalVariant.productsUnavailable) {
        acc.removedProducts.push(product);
      } else if (variant === OutdatedBundleModalVariant.productsChanged) {
        acc.hasProductsChanged = true;
      }

      return acc;
    },
    { removedProducts: [] as ProductType[], hasProductsChanged: false }
  );

  if (removedProducts.length > 0) {
    return {
      variant: OutdatedBundleModalVariant.productsUnavailable,
      removedProducts,
    };
  }

  return {
    variant: hasProductsChanged
      ? OutdatedBundleModalVariant.productsChanged
      : OutdatedBundleModalVariant.none,
    removedProducts: null,
  };
};

export {
  getEnergyModalVariant,
  getModalCharacteristics,
  getProductVariant,
  compareEnergyPrices,
};
